import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './docs.css';

// Convert markdown tables to HTML tables
const convertTablesToHtml = (markdown) => {
    const lines = markdown.split('\n');
    const result = [];
    let inTable = false;
    let tableLines = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        // Check if line is a table row (starts and ends with |)
        if (line.startsWith('|') && line.endsWith('|')) {
            if (!inTable) {
                inTable = true;
                tableLines = [];
            }
            tableLines.push(line);
        } else {
            if (inTable) {
                // End of table, convert to HTML
                result.push(convertTableToHtml(tableLines));
                inTable = false;
                tableLines = [];
            }
            result.push(lines[i]);
        }
    }

    // Handle table at end of content
    if (inTable && tableLines.length > 0) {
        result.push(convertTableToHtml(tableLines));
    }

    return result.join('\n');
};

const convertTableToHtml = (tableLines) => {
    if (tableLines.length < 2) return tableLines.join('\n');

    const parseRow = (line) => {
        return line
            .split('|')
            .slice(1, -1) // Remove empty first/last from split
            .map(cell => cell.trim());
    };

    const headerCells = parseRow(tableLines[0]);
    // Skip separator line (tableLines[1])
    const bodyRows = tableLines.slice(2);

    let html = '<div class="docs-table-wrapper"><table class="docs-table">';

    // Header
    html += '<thead><tr>';
    headerCells.forEach(cell => {
        html += `<th>${cell}</th>`;
    });
    html += '</tr></thead>';

    // Body
    html += '<tbody>';
    bodyRows.forEach(row => {
        const cells = parseRow(row);
        html += '<tr>';
        cells.forEach(cell => {
            html += `<td>${cell}</td>`;
        });
        html += '</tr>';
    });
    html += '</tbody></table></div>';

    return html;
};

// Documentation registry - add new docs here
const DOCS = {
    'kako-sistem-radi': {
        title: 'Kako sistem radi',
        file: '/docs/kako-sistem-radi.md',
        category: 'Uvod'
    },
    'scan-api': {
        title: 'Scan API',
        file: '/docs/scan-api.md',
        category: 'API Reference'
    }
    // Add more docs here:
    // 'campaigns-api': { title: 'Campaigns API', file: '/docs/campaigns-api.md', category: 'API Reference' },
};

const DEFAULT_DOC = 'kako-sistem-radi';

const Documentation = () => {
    const { slug } = useParams();
    const history = useHistory();
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const activeSlug = slug || DEFAULT_DOC;
    const currentDoc = DOCS[activeSlug];

    useEffect(() => {
        if (currentDoc) {
            setLoading(true);
            fetch(currentDoc.file)
                .then(response => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return response.text();
                })
                .then(text => {
                    setContent(text);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error loading doc:', err);
                    setContent('');
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [activeSlug, currentDoc]);

    // Redirect to default doc if no slug
    if (!slug) {
        return <Redirect to={`/docs/${DEFAULT_DOC}`} />;
    }

    // Group docs by category
    const categories = {};
    Object.entries(DOCS).forEach(([key, doc]) => {
        if (!categories[doc.category]) {
            categories[doc.category] = [];
        }
        categories[doc.category].push({ slug: key, ...doc });
    });

    return (
        <div className="docs-container">
            {/* Mobile toggle */}
            <button
                className="docs-sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
            >
                {sidebarOpen ? '✕' : '☰'} Menu
            </button>

            {/* Sidebar */}
            <aside className={`docs-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="docs-sidebar-header">
                    <h2>Dokumentacija</h2>
                </div>
                <nav className="docs-nav">
                    {Object.entries(categories).map(([category, docs]) => (
                        <div key={category} className="docs-category">
                            <h3 className="docs-category-title">{category}</h3>
                            <ul className="docs-list">
                                {docs.map(doc => (
                                    <li key={doc.slug}>
                                        <a
                                            href={`/docs/${doc.slug}`}
                                            className={`docs-link ${activeSlug === doc.slug ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                history.push(`/docs/${doc.slug}`);
                                                setSidebarOpen(false);
                                            }}
                                        >
                                            {doc.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>
            </aside>

            {/* Content */}
            <main className="docs-content">
                {loading ? (
                    <div className="docs-loading">Učitavanje...</div>
                ) : currentDoc && content ? (
                    <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        components={{
                            code({ node, inline, className, children, ...props }) {
                                const match = /language-(\w+)/.exec(className || '');
                                return !inline && match ? (
                                    <SyntaxHighlighter
                                        style={oneDark}
                                        language={match[1]}
                                        PreTag="div"
                                        {...props}
                                    >
                                        {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                ) : (
                                    <code className={className} {...props}>
                                        {children}
                                    </code>
                                );
                            }
                        }}
                    >
                        {convertTablesToHtml(content)}
                    </ReactMarkdown>
                ) : (
                    <div className="docs-not-found">
                        <h1>Stranica nije pronadjena</h1>
                        <p>Dokumentacija za "{activeSlug}" ne postoji.</p>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Documentation;
